import { api } from '../client';
import type { PageParams } from '@/types/api';
import type {
  AcademicStatus,
  AcademicSubject,
  AcademicYear,
  AcademicYearStatus,
  AcademicYearSummary,
  CalendarEvent,
  CalendarEventType,
  EducationLevel,
  Grade,
  GradeOffering,
  GradeSubject,
  GradeSummary,
  PeriodDefinition,
  PeriodType,
  Section,
  Subject,
  SubjectType,
  UUID,
} from '@/types/domain';
import type { StatusChangePayload } from './schools';

export interface AcademicYearPayload {
  code: string;
  name: string;
  startDate: string;
  endDate: string;
}

/** No campus: a grade is defined once for the school. */
export interface GradePayload {
  code: string;
  name: string;
  displayOrder: number;
  educationLevel: EducationLevel;
  description?: string;
}

export interface GradeOfferingPayload {
  academicYearId: UUID;
  campusId: UUID;
  gradeId: UUID;
  capacity?: number;
}

/** Only the capacity is editable — the other three are the offering's identity. */
export interface UpdateGradeOfferingPayload {
  capacity?: number;
}

export interface SectionPayload {
  gradeOfferingId: UUID;
  code: string;
  name: string;
  displayOrder?: number;
  capacity: number;
}

export interface SubjectPayload {
  code: string;
  name: string;
  shortName?: string;
  description?: string;
  subjectType: SubjectType;
}

export const academicYearsApi = {
  list: (params: PageParams & { schoolId?: UUID; status?: AcademicYearStatus }) =>
    api.getPage<AcademicYear>('/academic-years', { params }),
  picker: () => api.get<AcademicYearSummary[]>('/academic-years/picker'),
  /** Null when the school has not activated a year yet — not an error. */
  current: () => api.get<AcademicYear | null>('/academic-years/current'),
  get: (id: UUID) => api.get<AcademicYear>(`/academic-years/${id}`),
  create: (payload: AcademicYearPayload) => api.post<AcademicYear>('/academic-years', payload),
  update: (id: UUID, payload: AcademicYearPayload) =>
    api.put<AcademicYear>(`/academic-years/${id}`, payload),
  changeStatus: (id: UUID, payload: StatusChangePayload) =>
    api.patch<AcademicYear>(`/academic-years/${id}/status`, payload),
  setCurrent: (id: UUID) => api.patch<AcademicYear>(`/academic-years/${id}/set-current`),
  remove: (id: UUID) => api.delete(`/academic-years/${id}`),
};

export const gradesApi = {
  list: (
    params: PageParams & {
      schoolId?: UUID;
      educationLevel?: EducationLevel;
      status?: AcademicStatus;
      academicYearId?: UUID;
      campusId?: UUID;
    },
  ) => api.getPage<Grade>('/grades', { params }),
  picker: () => api.get<GradeSummary[]>('/grades/picker'),
  get: (id: UUID) => api.get<Grade>(`/grades/${id}`),
  create: (payload: GradePayload) => api.post<Grade>('/grades', payload),
  update: (id: UUID, payload: GradePayload) => api.put<Grade>(`/grades/${id}`, payload),
  changeStatus: (id: UUID, payload: StatusChangePayload) =>
    api.patch<Grade>(`/grades/${id}/status`, payload),
  remove: (id: UUID) => api.delete(`/grades/${id}`),
  subjects: (id: UUID) => api.get<GradeSubject[]>(`/grades/${id}/subjects`),
  addSubject: (id: UUID, payload: { subjectId: UUID; creditHours?: number; elective: boolean }) =>
    api.post<GradeSubject>(`/grades/${id}/subjects`, payload),
  removeSubject: (id: UUID, subjectId: UUID) => api.delete(`/grades/${id}/subjects/${subjectId}`),
};

export const gradeOfferingsApi = {
  list: (
    params: PageParams & {
      academicYearId?: UUID;
      campusId?: UUID;
      gradeId?: UUID;
      status?: AcademicStatus;
    },
  ) => api.getPage<GradeOffering>('/grade-offerings', { params }),
  get: (id: UUID) => api.get<GradeOffering>(`/grade-offerings/${id}`),
  create: (payload: GradeOfferingPayload) => api.post<GradeOffering>('/grade-offerings', payload),
  update: (id: UUID, payload: UpdateGradeOfferingPayload) =>
    api.put<GradeOffering>(`/grade-offerings/${id}`, payload),
  changeStatus: (id: UUID, payload: StatusChangePayload) =>
    api.patch<GradeOffering>(`/grade-offerings/${id}/status`, payload),
  remove: (id: UUID) => api.delete(`/grade-offerings/${id}`),
};

export const sectionsApi = {
  list: (
    params: PageParams & {
      gradeOfferingId?: UUID;
      academicYearId?: UUID;
      gradeId?: UUID;
      campusId?: UUID;
      status?: AcademicStatus;
    },
  ) => api.getPage<Section>('/sections', { params }),
  get: (id: UUID) => api.get<Section>(`/sections/${id}`),
  create: (payload: SectionPayload) => api.post<Section>('/sections', payload),
  update: (id: UUID, payload: SectionPayload) => api.put<Section>(`/sections/${id}`, payload),
  changeStatus: (id: UUID, payload: StatusChangePayload) =>
    api.patch<Section>(`/sections/${id}/status`, payload),
  remove: (id: UUID) => api.delete(`/sections/${id}`),
};

export const subjectsApi = {
  list: (params: PageParams & { subjectType?: SubjectType; status?: AcademicStatus }) =>
    api.getPage<Subject>('/subjects', { params }),
  picker: () => api.get<Subject[]>('/subjects/picker'),
  get: (id: UUID) => api.get<Subject>(`/subjects/${id}`),
  create: (payload: SubjectPayload) => api.post<Subject>('/subjects', payload),
  update: (id: UUID, payload: SubjectPayload) => api.put<Subject>(`/subjects/${id}`, payload),
  changeStatus: (id: UUID, payload: StatusChangePayload) =>
    api.patch<Subject>(`/subjects/${id}/status`, payload),
  remove: (id: UUID) => api.delete(`/subjects/${id}`),
};

// ---------------------------------------------- academic management (phase 4)

export interface AcademicSubjectPayload {
  gradeOfferingId: UUID;
  subjectId: UUID;
  mandatory: boolean;
  weeklyPeriods?: number;
}

export const academicSubjectsApi = {
  /** The curriculum for one grade offering — what that class is taught this year. */
  list: (gradeOfferingId: UUID, status?: AcademicStatus) =>
    api.get<AcademicSubject[]>('/academic-subjects', {
      params: { gradeOfferingId, ...(status !== undefined ? { status } : {}) },
    }),
  create: (payload: AcademicSubjectPayload) =>
    api.post<AcademicSubject>('/academic-subjects', payload),
  update: (id: UUID, payload: AcademicSubjectPayload) =>
    api.put<AcademicSubject>(`/academic-subjects/${id}`, payload),
  /**
   * There is no delete: timetable entries and teacher assignments point here, so
   * a subject is deactivated instead.
   */
  changeStatus: (id: UUID, status: AcademicStatus) =>
    api.patch<AcademicSubject>(`/academic-subjects/${id}/status`, { status }),
};

export interface PeriodPayload {
  campusId: UUID;
  name: string;
  /** `HH:mm` or `HH:mm:ss`. */
  startTime: string;
  endTime: string;
  periodType: PeriodType;
  displayOrder: number;
}

export const periodsApi = {
  list: (campusId: UUID) => api.get<PeriodDefinition[]>('/periods', { params: { campusId } }),
  create: (payload: PeriodPayload) => api.post<PeriodDefinition>('/periods', payload),
  update: (id: UUID, payload: PeriodPayload) => api.put<PeriodDefinition>(`/periods/${id}`, payload),
  changeStatus: (id: UUID, status: AcademicStatus) =>
    api.patch<PeriodDefinition>(`/periods/${id}/status`, { status }),
};

export interface CalendarEventPayload {
  academicYearId: UUID;
  /** Omit for a school-wide event. */
  campusId?: UUID;
  title: string;
  description?: string;
  eventType: CalendarEventType;
  startDate: string;
  endDate: string;
  holiday: boolean;
}

export const calendarApi = {
  /** Filtering by campus keeps school-wide events, which apply everywhere. */
  list: (params: { campusId?: UUID; from: string; to: string }) =>
    api.get<CalendarEvent[]>('/calendar-events', { params }),
  listForYear: (academicYearId: UUID) =>
    api.get<CalendarEvent[]>(`/calendar-events/by-year/${academicYearId}`),
  create: (payload: CalendarEventPayload) => api.post<CalendarEvent>('/calendar-events', payload),
  update: (id: UUID, payload: CalendarEventPayload) =>
    api.put<CalendarEvent>(`/calendar-events/${id}`, payload),
  /** Cancelled rather than removed: an event that was announced and called off is a fact. */
  cancel: (id: UUID) => api.delete<CalendarEvent>(`/calendar-events/${id}`),
};
